/**
 * Custom listbox UI for cart drawer variant <select> (hidden native control for existing theme JS).
 */
(() => {
  const ROOT = '[data-cart-drawer-variant-dd]';

  function closeDropdown(root) {
    const btn = root.querySelector('.cart-drawer-variant-dd__toggle');
    const list = root.querySelector('.cart-drawer-variant-dd__list');
    if (!btn || !list) return;
    root.classList.remove('cart-drawer-variant-dd--open');
    btn.setAttribute('aria-expanded', 'false');
    list.hidden = true;
    if (root._cddDocClick) {
      document.removeEventListener('click', root._cddDocClick);
      root._cddDocClick = null;
    }
    if (root._cddKeydown) {
      document.removeEventListener('keydown', root._cddKeydown);
      root._cddKeydown = null;
    }
  }

  function closeOthers(exceptRoot) {
    document.querySelectorAll(`${ROOT}.cart-drawer-variant-dd--open`).forEach((r) => {
      if (r !== exceptRoot) closeDropdown(r);
    });
  }

  function openDropdown(root) {
    closeOthers(root);
    const btn = root.querySelector('.cart-drawer-variant-dd__toggle');
    const list = root.querySelector('.cart-drawer-variant-dd__list');
    if (!btn || !list) return;
    root.classList.add('cart-drawer-variant-dd--open');
    btn.setAttribute('aria-expanded', 'true');
    list.hidden = false;

    const docClick = (evt) => {
      if (!root.contains(evt.target)) closeDropdown(root);
    };
    root._cddDocClick = docClick;
    document.addEventListener('click', docClick);

    const docKey = (evt) => {
      if (evt.key === 'Escape' && root.classList.contains('cart-drawer-variant-dd--open')) {
        closeDropdown(root);
        btn.focus();
      }
    };
    root._cddKeydown = docKey;
    document.addEventListener('keydown', docKey);

    const selected = list.querySelector('[role="option"][aria-selected="true"]') || list.querySelector('[role="option"]');
    if (selected) {
      window.requestAnimationFrame(() => selected.focus());
    }
  }

  function syncUIFromSelect(select) {
    const root = select.closest(ROOT);
    if (!root) return;
    const btn = root.querySelector('.cart-drawer-variant-dd__toggle');
    const list = root.querySelector('.cart-drawer-variant-dd__list');
    if (!btn || !list) return;
    const val = select.value;
    const optEl = select.selectedOptions[0];
    const label = optEl ? optEl.textContent.trim() : val;
    const valueSpan = btn.querySelector('.cart-drawer-variant-dd__value');
    if (valueSpan) valueSpan.textContent = label;
    const optName = select.getAttribute('aria-label') || '';
    btn.setAttribute('aria-label', `${optName}: ${label}`.trim());

    list.querySelectorAll('[role="option"]').forEach((li) => {
      const isSel = li.dataset.value === val;
      li.setAttribute('aria-selected', isSel ? 'true' : 'false');
    });
  }

  function getOptions(root) {
    const list = root.querySelector('.cart-drawer-variant-dd__list');
    return list ? Array.from(list.querySelectorAll('[role="option"]')) : [];
  }

  function focusOptionDelta(root, delta) {
    const opts = getOptions(root);
    if (!opts.length) return;
    const active = document.activeElement;
    let idx = opts.indexOf(active);
    if (idx < 0) {
      idx = opts.findIndex((o) => o.getAttribute('aria-selected') === 'true');
      if (idx < 0) idx = 0;
      else idx = (idx + delta + opts.length) % opts.length;
    } else {
      idx = (idx + delta + opts.length) % opts.length;
    }
    opts[idx].focus();
  }

  function bindRoot(root) {
    if (root.dataset.cddInit) return;
    root.dataset.cddInit = '1';

    const select = root.querySelector(
      'select.cart-drawer-variant-dd__native, select.cart-drawer-pairing-line__option-select'
    );
    const btn = root.querySelector('.cart-drawer-variant-dd__toggle');
    const list = root.querySelector('.cart-drawer-variant-dd__list');
    if (!select || !btn || !list) return;

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (root.classList.contains('cart-drawer-variant-dd--open')) closeDropdown(root);
      else openDropdown(root);
    });

    list.addEventListener('click', (e) => {
      const item = e.target.closest('[role="option"]');
      if (!item || !list.contains(item)) return;
      const v = item.dataset.value;
      if (v == null) return;
      if (select.value !== v) {
        select.value = v;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      syncUIFromSelect(select);
      closeDropdown(root);
      btn.focus();
    });

    btn.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const wasOpen = root.classList.contains('cart-drawer-variant-dd--open');
        if (!wasOpen) openDropdown(root);
        else focusOptionDelta(root, e.key === 'ArrowDown' ? 1 : -1);
        return;
      }
      if ((e.key === 'Enter' || e.key === ' ') && !root.classList.contains('cart-drawer-variant-dd--open')) {
        e.preventDefault();
        openDropdown(root);
      }
    });

    list.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeDropdown(root);
        btn.focus();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        focusOptionDelta(root, 1);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        focusOptionDelta(root, -1);
        return;
      }
      if (e.key === 'Home') {
        e.preventDefault();
        getOptions(root)[0]?.focus();
        return;
      }
      if (e.key === 'End') {
        e.preventDefault();
        const opts = getOptions(root);
        opts[opts.length - 1]?.focus();
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const active = document.activeElement;
        if (active && active.matches('[role="option"]')) active.click();
      }
    });

    syncUIFromSelect(select);
  }

  function initScope(container) {
    const scope = container && container.querySelectorAll ? container : document;
    if (scope === document) {
      document.querySelectorAll(ROOT).forEach(bindRoot);
    } else {
      scope.querySelectorAll(ROOT).forEach(bindRoot);
    }
  }

  window.theme = window.theme || {};
  window.theme.syncCartDrawerOptionDropdownUI = (selectEl) => {
    if (!selectEl || !selectEl.matches || !selectEl.matches('.cart-drawer-pairing-line__option-select')) return;
    syncUIFromSelect(selectEl);
  };
  window.initCartDrawerVariantDropdowns = initScope;

  document.addEventListener('DOMContentLoaded', () => initScope(document));

  document.addEventListener('on:cart:after-merge', (e) => {
    if (e.target && e.target.nodeType === 1) initScope(e.target);
  });

  document.addEventListener('cart-pairing:recommendations-loaded', (e) => {
    const scope = e.target && e.target.nodeType === 1 ? e.target : document;
    initScope(scope);
  });
})();
